import type { MetaHeadProps } from './types';
import type { ReactElement } from 'react';

import { useMemo } from 'react';

import { BaseMetaHead } from './BaseMetaHead';
import { withGalleryImage } from './structuredDataImage';
import { useAppContext } from '../../../utilities/context/static/AppContext';
import { usePDPContext } from '../../../utilities/context/static/PDPContext';
import { useProductDetailsImages } from '~/utilities/hooks/useProductDetailsImages';

export const PDPMetaHead = ({
  metadataContent,
  productStructuredData,
}: MetaHeadProps & { productStructuredData: string | null }): ReactElement => {
  const { country, productionHost } = useAppContext();
  const { product } = usePDPContext();
  const { image01 } = useProductDetailsImages();

  const metaTitle = `${
    metadataContent?.pageTitle || product.metaTitle || ''
  } ${country.toUpperCase()}`;

  const productImageUrl = image01?.url?.replace('{{dimensions}}', `h_630`);
  const structuredData = useMemo(
    () => productStructuredData && withGalleryImage(productStructuredData, image01?.url),
    [productStructuredData, image01?.url]
  );
  const description = metadataContent?.pageDescription || product.featuresIntro || '';

  return (
    <BaseMetaHead
      title={metaTitle}
      description={description}
      robots={metadataContent?.robots}
      canonicalUrl={product.canonicalUrl ? `${productionHost}${product.canonicalUrl}` : undefined}
      metaTags={metadataContent?.metaTags}
      iosDeepLink={product.appUrl || ''}
      trailingChildren={
        structuredData && (
          <script
            id="structured-data-pdp"
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: structuredData }}
          />
        )
      }
    >
      <meta key="meta-og:title" property="og:title" content={metaTitle} />
      <meta key="meta-og:type" property="og:type" content="product" />
      <meta
        key="meta-og:url"
        property="og:url"
        content={`${productionHost}${product.canonicalUrl}`}
      />
      <meta key="meta-og:image" property="og:image" content={productImageUrl} />
      <meta
        key="meta-og:image:secure_url"
        property="og:image:secure_url"
        content={productImageUrl}
      />
      <meta key="meta-og:description" property="og:description" content={description} />
    </BaseMetaHead>
  );
};
